import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo } from "react";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import ModalForm from "components/modal-form";
import {
  MULTIPLE_CPT_CODES_FEATURE,
  PRODUCT_CATEGORIES_FEATURE
} from "constants/feature-flags";
import { PRODUCT_SUB_TYPES } from "constants/products";
import { PRODUCT_TYPES } from "constants/sales";
import ProductForm, {
  ProductFormValues
} from "features/inventory/products/product-form.component";
import { formatProductFormValues } from "features/inventory/products/products.util";
import { useLazyGetMasterProductQuery } from "services/master-products";
import { useAddProductMutation } from "services/products";
import { type MasterProduct } from "services/types/master-products.type";
import { type ProductCatalogOption } from "services/types/products.type";

type NewProductAdditionModalProps = {
  name?: string;
  type: number;
  subtype?: number | number[] | null;
  // Overrides the derived "Add New <subtype/type>" heading. Used where the
  // subtype label alone reads oddly — e.g. "Repair" vs "Repair Service".
  title?: string;
  isOpen: boolean;
  onCancel: () => void;
  onSave: (option: ProductCatalogOption) => void;
};
export default function NewProductAdditionModal({
  name = "",
  type,
  subtype,
  title: titleOverride,
  isOpen,
  onCancel,
  onSave
}: NewProductAdditionModalProps) {
  const dispatch = useAppDispatch();
  const flags = useFlags();
  const multipleCptCodesFeature = Boolean(flags[MULTIPLE_CPT_CODES_FEATURE]);
  const productCategoriesFeature = Boolean(flags[PRODUCT_CATEGORIES_FEATURE]);
  const [addProduct, { isLoading: isSubmitting }] = useAddProductMutation();

  const [getMasterProductById] = useLazyGetMasterProductQuery();

  const onProductSave = useCallback(
    (values: ProductFormValues) => {
      addProduct(
        formatProductFormValues(
          values,
          multipleCptCodesFeature,
          productCategoriesFeature
        )
      ).then((productResponse) => {
        if ("error" in productResponse) {
          dispatch(
            setCustomMessage(
              <ErrorSnackbarContent response={productResponse} />
            )
          );
          dispatch(setOpen(true));
        } else {
          getMasterProductById(
            Number(productResponse.data?.master_product)
          ).then((masterProductResponse) => {
            if ("error" in masterProductResponse) {
              dispatch(
                setCustomMessage(
                  <ErrorSnackbarContent response={masterProductResponse} />
                )
              );
              dispatch(setOpen(true));
            } else {
              dispatch(setCustomMessage("Product created"));
              dispatch(setOpen(true));
              onSave({
                ...productResponse.data,
                master_product: masterProductResponse.data as MasterProduct,
                display_name:
                  productResponse.data?.display_name ??
                  masterProductResponse.data?.display_name
              });
            }
          });
        }
      });
    },
    [
      addProduct,
      dispatch,
      getMasterProductById,
      multipleCptCodesFeature,
      productCategoriesFeature,
      onSave
    ]
  );

  const title =
    titleOverride ??
    `Add New ${
      !Array.isArray(subtype)
        ? PRODUCT_SUB_TYPES.getFromCode(subtype)
        : PRODUCT_TYPES.getFromCode(type)
    }`;

  const initialValues = useMemo(
    () => ({
      type: String(type),
      subtype: !Array.isArray(subtype) ? String(subtype) : undefined,
      name
    }),
    [name, subtype, type]
  );

  return (
    <ModalForm
      isOpen={isOpen}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      title={title}
      hideBackdrop={false}
      formName="catalog-product"
      maxWidth="lg"
      sx={{ maxWidth: "1400px" }}
      zIndex={1501}
    >
      <ProductForm
        isNew={true}
        isEditable={true}
        isSubmitting={isSubmitting}
        embeddedType={type}
        embeddedSubtype={subtype}
        onSubmit={onProductSave}
        onCancel={onCancel}
        initialValues={initialValues}
      />
    </ModalForm>
  );
}
